import type { AudioTrackFieldsFragment } from "@/generated/graphql";
import { IconButton } from "@ai-teach/ui-web";
import { useState } from "react";
import { MdCheck, MdClose, MdDelete, MdDragIndicator, MdEdit } from "react-icons/md";

interface AudioTrackRowProps {
  track: AudioTrackFieldsFragment;
  index: number;
  onRename: (id: string, name: string) => Promise<unknown>;
  onDelete: (id: string) => void;
  disabled: boolean;
}

function formatSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * One audio file in an audio folder: drag handle, inline-renameable title, native preview
 * player and delete. The `<audio>` element is deliberately the browser's own — this is an
 * admin preview, not the student experience, and a custom player would be a maintenance
 * cost for no teaching benefit.
 */
export function AudioTrackRow({ track, index, onRename, onDelete, disabled }: AudioTrackRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draftName, setDraftName] = useState(track.name);

  const handleSave = async () => {
    const trimmed = draftName.trim();
    if (trimmed && trimmed !== track.name) {
      await onRename(track.id, trimmed);
    } else {
      setDraftName(track.name);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setDraftName(track.name);
    setIsEditing(false);
  };

  return (
    <div
      id={`audio-track-${track.id}`}
      className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 dark:border-gray-800 bg-surface shadow-sm"
    >
      <div className="flex flex-col items-center gap-0.5 px-1.5 text-gray-400 cursor-grab select-none">
        <MdDragIndicator className="w-5 h-5" aria-hidden="true" />
        <span className="text-xs font-semibold leading-none">{index + 1}</span>
      </div>

      <div className="flex-1 min-w-0">
        <div className="group/title flex items-center gap-2">
          <input
            type="text"
            value={draftName}
            readOnly={!isEditing}
            aria-label="Track name"
            className={`w-full truncate bg-transparent border-none outline-none card-title ${
              isEditing ? "ring-2 ring-blue-500 rounded px-2 py-1" : "px-0"
            }`}
            onChange={(e) => setDraftName(e.target.value)}
            onClick={() => {
              if (!isEditing) setIsEditing(true);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
              else if (e.key === "Escape") handleCancel();
            }}
            onBlur={handleSave}
          />

          {!isEditing ? (
            <IconButton
              aria-label="Edit track name"
              onClick={() => setIsEditing(true)}
              variant="ghost"
              size="sm"
              className="opacity-0 group-hover/title:opacity-100 transition-opacity duration-200 text-gray-400 flex-shrink-0"
            >
              <MdEdit className="w-4 h-4" aria-hidden="true" />
            </IconButton>
          ) : (
            <div className="flex gap-1 flex-shrink-0">
              <IconButton
                aria-label="Save"
                onClick={handleSave}
                variant="ghost"
                size="sm"
                className="text-success-500 hover:bg-success-700/20"
              >
                <MdCheck className="w-4 h-4" />
              </IconButton>
              <IconButton
                aria-label="Cancel"
                onClick={handleCancel}
                variant="ghost"
                size="sm"
                className="text-error-500 hover:bg-error-700/20"
              >
                <MdClose className="w-4 h-4" />
              </IconButton>
            </div>
          )}
        </div>

        <div className="mt-1 flex items-center gap-2 caption">
          <span className="truncate">{track.audio.filename}</span>
          <span aria-hidden="true">·</span>
          <span>{formatSize(track.audio.size)}</span>
        </div>
      </div>

      {/* biome-ignore lint/a11y/useMediaCaption: teacher-facing preview of an uploaded file */}
      <audio
        controls
        preload="none"
        src={track.audio.url}
        className="max-w-xs w-64 flex-shrink-0"
      />

      <IconButton
        aria-label={`Delete ${track.name}`}
        onClick={() => onDelete(track.id)}
        variant="ghost"
        size="sm"
        disabled={disabled}
        className="text-error-500 hover:bg-error-700/20 flex-shrink-0"
      >
        <MdDelete className="w-5 h-5" />
      </IconButton>
    </div>
  );
}
